import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getApartments,
  createApartment,
  Apartment as ApartmentType
} from '../api.apartments';
import { submitApartmentLimitRequest } from '../api.billing';
import { getMyContacts, Contact } from '../api.contacts';
import ApartmentListItem from '../components/ApartmentListItem';
import Pagination from '../components/Pagination';
import PageHeaderControls from '../components/PageHeaderControls';
import useIsMobile from '../hooks/useIsMobile';
import AppButton from '../components/AppButton';
import { useLang } from '../i18n/LanguageContext';
import { apartmentsText } from '../i18n/pages/apartments';
import { common } from '../i18n/pages/common';
import { deriveSetupStep } from '../utils/setupStep';

const getToken = () => localStorage.getItem('token') || '';
const PAGE_SIZE_STORAGE_KEY = 'apartments.pageSize';
const DEFAULT_PAGE_SIZE = 10;
const APARTMENTS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SORT_STORAGE_KEY = 'apartments.sortBy';

type ApartmentSort = 'id' | 'name';
const emptyApartment: ApartmentType = {
  name: '',
  internalName: '',
  displayName: '',
  address: '',
  capacity: 1,
  description: '',
  isActive: true,
  autoSendToUbyport: false,
};

const Apartments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const t = apartmentsText[lang];
  const tc = common[lang];

  const [apartments, setApartments] = useState<ApartmentType[]>([]);
  const [filteredApartments, setFilteredApartments] = useState<ApartmentType[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ApartmentType>(emptyApartment);

  // Filters/sort/page live in the URL so opening an apartment and pressing Back restores this
  // exact filtered view instead of resetting to the unfiltered list.
  const [searchParams, setSearchParams] = useSearchParams();
  const paramNum = (key: string): number | undefined => {
    const v = searchParams.get(key);
    if (!v) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const [filter, setFilter] = useState({
    name: searchParams.get('name') || '',
    contactId: paramNum('contactId'),
    isActive: searchParams.has('isActive') ? searchParams.get('isActive') === 'true' : undefined as boolean | undefined,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(paramNum('page') ?? 0);
  const pageResetMounted = useRef(false);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [limitModal, setLimitModal] = useState<{ currentCount: number; currentLimit: number } | null>(null);
  const [limitMessage, setLimitMessage] = useState('');
  const [limitRequestSent, setLimitRequestSent] = useState(false);
  const [limitRequestError, setLimitRequestError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<ApartmentSort>(() => {
    const fromUrl = searchParams.get('sortBy');
    if (fromUrl === 'id' || fromUrl === 'name') return fromUrl;
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    return saved === 'id' ? 'id' : 'name';
  });
  const isMobile = useIsMobile();

  // Same rule as the nav cue, read from this page's own list — no extra request. The ring only
  // appears for someone who arrived by clicking the cue, not on every visit to the page.
  const setupStep = loading ? null : deriveSetupStep(apartments);
  // Captured once: this page's own filter/URL sync replaces the history entry on mount, which drops
  // router state, so reading location.state live would lose the flag a moment after arriving.
  const [arrivedViaCue, setArrivedViaCue] = useState(() => !!(location.state as { setupCue?: boolean } | null)?.setupCue);
  // Clicking the cued link while already on this page pushes a new entry with the state but does
  // not remount, so the initialiser above never sees it.
  useEffect(() => {
    if ((location.state as { setupCue?: boolean } | null)?.setupCue) setArrivedViaCue(true);
  }, [location.key, location.state]);
  const spotClass = arrivedViaCue ? 'setup-cue-spot' : undefined;
  // Only in the second step: an established manager may leave apartments without a calendar on purpose.
  const connectCalendarCta = (a: ApartmentType) =>
    setupStep === 'CONNECT_CALENDAR' && a.calendarSyncOk == null && a.id ? (
      <Link
        to={`/apartments/${a.id}?tab=calendar`}
        className={spotClass}
        style={{ display: 'inline-flex', alignItems: 'center', minHeight: 32, padding: '6px 12px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 700, background: '#dbeafe', color: '#1e3a8a', textDecoration: 'none', whiteSpace: 'nowrap' }}
      >
        {t.connectCalendarCta}
      </Link>
    ) : null;

  const fetchApartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getApartments(getToken());
      setApartments(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load apartments');
    } finally {
      setLoading(false);
    }
  };
  const fetchContacts = async () => {
    try {
      const data = await getMyContacts(getToken());
      setContacts(data);
    } catch (err: any) {
      console.error('Failed to load contacts:', err);
    }
  };
  useEffect(() => {
    fetchApartments();
    fetchContacts();
  }, []);
  useEffect(() => {
    let filtered = [...apartments];
    if (filter.name) {
      filtered = filtered.filter(apt => apt.name.toLowerCase().includes(filter.name.toLowerCase()));
    }
    if (filter.contactId !== undefined) {
      if (filter.contactId === 0) {
        filtered = filtered.filter(apt => !apt.assignedContact);
      } else {
        filtered = filtered.filter(apt => apt.assignedContact?.id === filter.contactId);
      }
    }
    if (filter.isActive !== undefined) {
      filtered = filtered.filter(apt => apt.isActive === filter.isActive);
    }
    setFilteredApartments(filtered);
  }, [apartments, filter]);

  const sortedApartments = [...filteredApartments].sort((a, b) =>
    sortBy === 'id' ? (a.id ?? 0) - (b.id ?? 0) : a.name.localeCompare(b.name),
  );
  const totalPages = Math.ceil(sortedApartments.length / pageSize);
  const pagedApartments = sortedApartments.slice(page * pageSize, (page + 1) * pageSize);

  // Sort/page-size/filter changes snap back to page 1 — but not on the initial mount, where
  // `page` may have just been restored from the URL and shouldn't be stomped back to 0.
  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortBy);
    if (pageResetMounted.current) setPage(0);
  }, [sortBy]);

  useEffect(() => {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (APARTMENTS_PAGE_SIZE_OPTIONS.includes(parsed)) {
        setPageSize(parsed);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    if (pageResetMounted.current) setPage(0);
  }, [pageSize]);

  useEffect(() => {
    if (pageResetMounted.current) setPage(0);
  }, [filter]);

  useEffect(() => {
    pageResetMounted.current = true;
  }, []);

  // Keep the URL in sync with filters/sort/page (replace, not push, so typing doesn't spam
  // history) — this is what lets Back-from-detail land on the same filtered view.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const set = (key: string, value: string | number | undefined | null) => {
        if (value === undefined || value === null || value === '') p.delete(key);
        else p.set(key, String(value));
      };
      set('name', filter.name);
      set('contactId', filter.contactId);
      set('isActive', filter.isActive === undefined ? undefined : String(filter.isActive));
      set('sortBy', sortBy !== 'name' ? sortBy : undefined);
      set('page', page ? page : undefined);
      return p;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortBy, page]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilter(f => ({
      ...f,
      [name]: name === 'contactId'
        ? (value === '' ? undefined : (value === '0' ? 0 : parseInt(value)))
        : name === 'isActive'
        ? (value === '' ? undefined : value === 'true')
        : value,
    }));
  };
  const handleClearFilters = () => {
    setFilter({ name: '', contactId: undefined, isActive: undefined });
  };
  const handleSearch = () => {
    setPage(0);
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else if (name === 'contactId') {
      const cid = parseInt(value) || 0;
      setForm(f => ({ ...f, assignedContact: cid > 0 ? { id: cid, name: '' } : undefined }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };
  const handleAdd = () => {
    setForm(emptyApartment);
    setShowForm(true);
    setSuccess(null);
    setError(null);
  };
  // Carry the current filters/sort/page along so returning from the detail page (via its
  // `returnTo`-aware Back link) restores this exact view.
  const getDetailUrl = (apartmentId: number) =>
    `/apartments/${apartmentId}?returnTo=${encodeURIComponent('/apartments' + location.search)}`;
  const handleOpenDetail = (apartment: ApartmentType) => {
    if (!apartment.id) return;
    navigate(getDetailUrl(apartment.id));
  };
  const handleViewReservations = (apartmentId: number) => {
    navigate('/reservations', { state: { apartmentId } });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const dataToSend: any = {
        internalName: form.internalName,
        displayName: form.displayName,
        address: form.address,
        capacity: form.capacity,
        description: form.description,
        isActive: form.isActive,
        assignedContact: form.assignedContact?.id ? { id: form.assignedContact.id, name: '' } : null,
      };

      await createApartment(dataToSend, getToken());
      setSuccess(t.apartmentCreated);
      setShowForm(false);
      fetchApartments();
    } catch (err: any) {
      if (err.response?.status === 422) {
        const currentCount = apartments.length;
        setLimitModal({ currentCount, currentLimit: currentCount });
        setLimitMessage('');
        setLimitRequestSent(false);
        setLimitRequestError(null);
        setShowForm(false);
      } else {
        setError(err.response?.data?.message || err.message || 'Save failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLimitRequest = async () => {
    if (!limitModal) return;
    setLimitRequestError(null);
    try {
      await submitApartmentLimitRequest(limitModal.currentCount, limitModal.currentLimit, limitMessage, getToken());
      setLimitRequestSent(true);
    } catch (err: any) {
      setLimitRequestError(err.response?.data?.message || err.message || 'Failed to submit request');
    }
  };

  return (
    <div className="page-container">
      <PageHeaderControls
        title={t.pageTitle}
        addLabel={t.addApartment}
        onAdd={handleAdd}
        filterOpen={filterOpen}
        onToggleFilter={() => setFilterOpen((v) => !v)}
        filterSectionId="apartment-filter-section"
        showFiltersLabel={tc.showFilters}
        hideFiltersLabel={tc.hideFilters}
        searchLabel={tc.search}
        onSearch={handleSearch}
        clearFiltersLabel={tc.clearFilters}
        onClearFilters={handleClearFilters}
      >
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>{t.filterTitle}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.filterName}</label>
            <input type="text" name="name" value={filter.name} onChange={handleFilterChange} placeholder={t.searchByName} style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #cbd5e0', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.filterContact}</label>
            <select className="form-control" name="contactId" value={filter.contactId ?? ''} onChange={handleFilterChange}>
              <option value="">{t.filterAllContacts}</option>
              <option value="0">{t.filterNoContact}</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.tableStatus}:</label>
            <select className="form-control" name="isActive" value={filter.isActive === undefined ? '' : filter.isActive.toString()} onChange={handleFilterChange}>
              <option value="">{t.filterAll}</option>
              <option value="true">{t.filterActive}</option>
              <option value="false">{t.filterInactive}</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.9rem' }}>{t.sortBy}</label>
            <select className="form-control" value={sortBy} onChange={(e) => setSortBy(e.target.value as ApartmentSort)}>
              <option value="name">{t.sortName}</option>
              <option value="id">{t.sortId}</option>
            </select>
          </div>
        </div>
      </PageHeaderControls>
      {success && <div style={{ color: 'green', marginBottom: 12, padding: 12, background: '#f0fff4', borderRadius: 6 }}>{success}</div>}
      {error && <div style={{ color: 'red', marginBottom: 12, padding: 12, background: '#fff5f5', borderRadius: 6 }}>{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: 24 }} className="form-card">
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">{t.formInternalName}</label>
              <input className="form-control" name="internalName" maxLength={100} value={form.internalName || ''} onChange={handleFormChange} />
            </div>
            <div className="form-field">
              <label className="form-label">{t.formDisplayName}</label>
              <input className="form-control" name="displayName" maxLength={100} value={form.displayName} onChange={handleFormChange} required />
            </div>
            <div className="form-field">
              <label className="form-label">{t.formAddress}</label>
              <input className="form-control" name="address" value={form.address} onChange={handleFormChange} required />
            </div>
            <div className="form-field">
              <label className="form-label">{t.formCapacity}</label>
              <input className="form-control" name="capacity" type="number" min={1} value={form.capacity} onChange={handleFormChange} required />
            </div>
            <div className="form-field">
              <label className="form-label">{t.formContact}</label>
              <select className="form-control" name="contactId" value={form.assignedContact?.id || 0} onChange={handleFormChange}>
                <option value="0">{t.formNoContact}</option>
                {contacts.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t.formDescription}</label>
              <textarea className="form-control" name="description" value={form.description} onChange={handleFormChange} />
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label"><input name="isActive" type="checkbox" checked={form.isActive} onChange={handleFormChange} /> {t.formActive}</label>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>{isSubmitting ? '…' : t.createBtn}</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn btn-sm btn-muted">Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ padding: 20, color: '#718096' }}>{t.loading}</div>}

      {!loading && (
        <div
          style={{
            marginBottom: 12,
            fontSize: '0.9rem',
            color: '#718096',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span>
            {t.showing(pagedApartments.length, sortedApartments.length)}
            {totalPages > 1 && ` ${t.pageInfo(page + 1, totalPages)}`}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
              <span>{t.itemsPerPage}</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e0', background: '#fff' }}
              >
                {APARTMENTS_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {!loading && apartments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🏠</div>
          <h2 style={{ margin: '0 0 12px', fontSize: '1.4rem', color: '#1a202c' }}>{t.emptyTitle}</h2>
          <p style={{ color: '#718096', marginBottom: 28, maxWidth: 440, margin: '0 auto 28px', lineHeight: 1.6 }}>{t.emptyBody}</p>
          <button
            onClick={handleAdd}
            className={setupStep === 'ADD_APARTMENT' ? spotClass : undefined}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#38a169', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
          >
            + {t.addApartment}
          </button>
        </div>
      ) : !loading && sortedApartments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 24px', background: '#fff', borderRadius: 8, color: '#718096', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {t.emptyNoResults}
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pagedApartments.map((a) => (
            <ApartmentListItem
              key={a.id}
              apartment={a}
              compact={true}
              onViewReservations={handleViewReservations}
              onOpenDetail={handleOpenDetail}
              calendarCta={connectCalendarCta(a)}
            />
          ))}
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 20 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8 }}>
              <thead>
                <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableId}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableName}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableAddress}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableCapacity}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableContact}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableStatus}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableCalendarSync}</th>
                  <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>{t.tableActions}</th>
                </tr>
              </thead>
              <tbody>
                {pagedApartments.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 12, color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.9rem' }}>{a.id}</td>
                    <td style={{ padding: 12, fontWeight: 600 }}>
                      {a.id ? (
                        <Link to={getDetailUrl(a.id)} style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                          {a.name}
                        </Link>
                      ) : (
                        a.name
                      )}
                    </td>
                    <td style={{ padding: 12 }}>{a.address}</td>
                    <td style={{ padding: 12 }}>{a.capacity}</td>
                    <td style={{ padding: 12 }}>{a.assignedContact ? a.assignedContact.name : t.contactYou}</td>
                    <td style={{ padding: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        background: a.isActive ? '#c6f6d5' : '#fed7d7',
                        color: a.isActive ? '#22543d' : '#742a2a',
                      }}>
                        {a.isActive ? t.statusActive : t.statusInactive}
                      </span>
                    </td>
                    <td style={{ padding: 12 }}>
                      {a.calendarSyncOk === false ? (
                        <Link
                          to={a.id ? `${getDetailUrl(a.id)}&tab=calendar` : '#'}
                          title={t.calendarSyncErrorHint}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            background: '#fed7d7',
                            color: '#742a2a',
                            textDecoration: 'none',
                          }}
                        >
                          ⚠️ {t.calendarSyncError}
                        </Link>
                      ) : a.calendarSyncOk === true ? (
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 999,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            background: '#c6f6d5',
                            color: '#22543d',
                          }}
                        >
                          {t.calendarSyncOk}
                        </span>
                      ) : (
                        connectCalendarCta(a) ?? <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 12, display: 'flex', gap: 8 }}>
                      <AppButton
                        type="button"
                        size="standard"
                        variant="green"
                        onClick={() => handleViewReservations(a.id!)}
                      >
                        {t.reservations}
                      </AppButton>
                      <AppButton type="button" size="standard" variant="blue" onClick={() => handleOpenDetail(a)}>
                        {t.btnEdit}
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      {limitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 460, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            {limitRequestSent ? (
              <>
                <div style={{ fontSize: '2rem', marginBottom: 12, textAlign: 'center' }}>✓</div>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>{t.limitSentTitle}</h2>
                <p style={{ color: '#4a5568', fontSize: '0.9rem', textAlign: 'center', marginBottom: 20 }}>
                  {t.limitSentBody}
                </p>
                <button onClick={() => setLimitModal(null)} style={{ width: '100%', padding: '10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Close</button>
              </>
            ) : (
              <>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: 8 }}>{t.limitTitle}</h2>
                <p style={{ color: '#4a5568', fontSize: '0.9rem', marginBottom: 16 }}>
                  {t.limitBody(limitModal.currentLimit)}
                </p>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>
                  {t.limitMessageLabel} <span style={{ color: '#a0aec0', fontWeight: 400 }}>{t.limitMessageOptional}</span>
                </label>
                <textarea
                  value={limitMessage}
                  onChange={e => setLimitMessage(e.target.value)}
                  placeholder={t.limitMessagePlaceholder}
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e0', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 }}
                />
                {limitRequestError && (
                  <div style={{ color: '#c53030', fontSize: '0.85rem', marginBottom: 10 }}>{limitRequestError}</div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleLimitRequest} style={{ flex: 1, padding: '10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                    {t.limitRequestBtn}
                  </button>
                  <button onClick={() => setLimitModal(null)} style={{ padding: '10px 16px', background: '#f7fafc', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Apartments;
